/**
 * Albanian — the default language.
 *
 * This file is the reference dictionary: `en.js` mirrors its keys, and a key
 * missing from English falls back to the string here.
 *
 * Keys are grouped by surface and kept flat, so a grep for the key finds both the
 * translation and its single use site.
 */
const sq = {
  /* ── sidebar / nav ─────────────────────────────────────────────────── */
  'nav.overview': 'Përmbledhje',
  'nav.inbox': 'Veprimet',
  'nav.monthly': 'Regjistrimi mujor',
  'nav.collect': 'Mbledhja e të dhënave',
  'nav.s1': 'Scope 1 — direkte',
  'nav.s2': 'Scope 2 — energji e blerë',
  'nav.s3': 'Scope 3 — zinxhiri i vlerës',
  'nav.stationary': 'Djegja stacionare',
  'nav.mobile': 'Djegja mobile',
  'nav.process': 'Emetimet nga proceset',
  'nav.electricity': 'Fatura e rrymës',
  'nav.heat': 'Ngrohje & ftohje',
  'nav.travel': 'Udhëtime & komutim',
  'nav.records': 'Regjistrimet',
  'nav.facilities': 'Ndërtesat',
  'nav.explorer': 'Eksploruesi i emetimeve',
  'nav.quality': 'Cilësia e të dhënave',
  'nav.reports': 'Raportet',
  'nav.reportsDetail': 'Raporti i detajuar',
  'nav.group.collect': 'Mblidh',
  'nav.group.measure': 'Mat',
  'nav.group.report': 'Raporto',
  'nav.group.cbam': 'CBAM',
  'nav.cbam': 'Përmbledhja CBAM',
  'nav.cbamInstallations': 'Instalimet',
  'nav.cbamGoods': 'Mallrat & prekursorët',
  'nav.admin.users': 'Përdoruesit & planet',
  'nav.admin.tokens': 'Tokenat e regjistrimit',

  /* ── chrome ────────────────────────────────────────────────────────── */
  'chrome.search': 'Kërko ose kalo te…',
  'chrome.period': 'Periudha raportuese',
  'chrome.account': 'Llogaria',
  'chrome.accountPassword': 'Llogaria & fjalëkalimi',
  'chrome.signOut': 'Dil',
  'chrome.owner': 'Pronar',
  'chrome.admin': 'Admin',
  'chrome.language': 'Gjuha',
  'chrome.workspace': 'Hapësira e punës',
  'chrome.expandSidebar': 'Zgjero shiritin anësor',
  'chrome.collapseSidebar': 'Palos shiritin anësor',
  'chrome.breadcrumb': 'Shtegu',
  'chrome.goTo': 'Shko te',
  'chrome.collectData': 'Regjistro të dhëna',

  /* ── shared ────────────────────────────────────────────────────────── */
  'common.loading': 'Duke u ngarkuar…',
  'common.retry': 'Provo përsëri',
  'common.tco2e': 't CO₂e',
  'common.months': 'muaj',
  // Month ledger + upload modal
  'ledger.title': 'Të regjistruara',
  'ledger.emptyHint': 'Asnjë aktivitet ende — regjistro të parin.',
  'ledger.confirmDelete': 'Fshi këtë regjistrim? Emetimet e tij hiqen nga raporti.',
  'ledger.deleteFailed': 'Fshirja dështoi. Provoni përsëri.',
  'ledger.prevMonth': 'Muaji i mëparshëm',
  'ledger.nextMonth': 'Muaji tjetër',
  'upload.badFormat': '{name}: Formati nuk mbështetet',
  'upload.tooBig': '{name}: Skedari është më i madh se 10MB',
  'upload.dropText': 'Lësho faturën këtu ose kërko',
  'upload.dropHint': 'PNG, JPG, PDF deri në 10MB',
  'upload.single': 'Ngarko faturën',
  'upload.bulk': 'Ngarko faturat',
  'common.all': 'Të gjitha',
  'common.none': '—',

  /* ── overview ──────────────────────────────────────────────────────── */
  'overview.title': 'Përmbledhje',
  'overview.desc': 'Gjurma e karbonit e kompanisë{year}, e konsoliduar për {count} {facilities}. Kufiri sipas kontrollit operacional, GHG Protocol.',
  'overview.facility': 'ndërtesë',
  'overview.facilities': 'ndërtesa',
  'overview.loading': 'Duke ngarkuar gjurmën e karbonit…',
  'overview.loadError': 'Nuk u ngarkua përmbledhja.',
  'overview.reports': 'Raportet',
  'overview.addData': 'Shto të dhëna',
  'overview.noData': 'Ende asnjë emetim i regjistruar në këtë llogari.',
  'overview.noDataBody': 'Filloni me regjistrimin mujor të udhëhequr, ose shtoni një aktivitet nga menyja e mbledhjes së të dhënave.',
  'overview.kpi.total': 'Totali — {period}',
  'overview.kpi.currentPeriod': 'periudha aktuale',
  'overview.kpi.intensity': 'Intensiteti i emetimeve',
  'overview.kpi.floorArea': '{area} m² sipërfaqe',
  'overview.kpi.noFloorArea': 'Pa sipërfaqe të regjistruar',
  'overview.kpi.completeness': 'Plotësia e inventarit',
  'overview.kpi.completenessFoot': '{complete} të plota · {partial} të pjesshme',
  'overview.kpi.vsBaseline': 'kundrejt {year}',
  'overview.kpi.baseline': 'vitit bazë',
  'overview.kpi.noBaseline': 'Pa të dhëna të vitit bazë për krahasim',
  'overview.kpi.onTrack': 'Në rrugë të mbarë për {year}',
  'overview.kpi.offTrack': 'Jashtë trajektores për {year}',
  'overview.scopeSplit': 'Gjurma sipas Scope-it',
  'overview.scopeSplitSub': 'Kategoritë e GHG Protocol · 12 muajt e fundit',
  'overview.scope1': 'Scope 1 — direkte',
  'overview.scope2': 'Scope 2 — energji e blerë',
  'overview.scope3': 'Scope 3 — zinxhiri i vlerës',
  'overview.monthly': 'Emetimet mujore',
  'overview.coverage': 'Mbulueshmëria e të dhënave',

  /* ── action inbox ──────────────────────────────────────────────────── */
  'inbox.title': 'Veprimet',
  'inbox.desc': 'Gjithçka që qëndron mes jush dhe një inventari të gatshëm për auditim, më urgjentja e para.',
  'inbox.cardTitle': 'Veprimet',
  'inbox.cardSub': 'Çfarë qëndron mes jush dhe një inventari të gatshëm për auditim',
  'inbox.open': '{count} të hapura',
  'inbox.clear': 'Pastër',
  'inbox.seeAll': 'Shiko të {count} veprimet',
  'inbox.openInbox': 'Hap veprimet',
  'inbox.emptyTitle': 'Asgjë nuk e bllokon inventarin tuaj',
  'inbox.emptyBody': 'Çdo ndërtesë ka të dhëna për çdo muaj të kaluar këtë vit.',
  'inbox.emptyBodyYear': 'Çdo ndërtesë ka të dhëna për të {count} muajt e kaluar në {year}.',
  'inbox.loading': 'Duke llogaritur çfarë mungon…',
  'inbox.stat.open': 'Veprime të hapura',
  'inbox.stat.openFootNone': 'Asgjë e papërfunduar',
  'inbox.stat.openFoot': 'Çdo rresht më poshtë është një rregullim',
  'inbox.stat.covered': 'Muaj-ndërtesë të mbuluara · {year}',
  'inbox.stat.coveredFoot': '{pct}% e muajve të kaluar deri tani',
  'inbox.stat.noFacilities': 'Ende pa ndërtesa',
  'inbox.stat.partial': 'Muaj që i mungon një Scope',
  'inbox.stat.partialFoot': 'Të regjistruar, por vetëm Scope 1 ose Scope 2',
  'inbox.elapsedNote': 'Mbulueshmëria matet vetëm ndaj muajve që kanë kaluar në {year} — muajt e ardhshëm nuk numërohen si boshllëqe.',
  'inbox.group.missing': 'Të dhëna që mungojnë',
  'inbox.group.missingSub': 'Asnjë regjistrim i asnjë lloji për këta muaj-ndërtesë',
  'inbox.group.partial': 'Muaj të papërfunduar',
  'inbox.group.partialSub': 'Ekziston një regjistrim, por një Scope mungon, kështu që muaji e nënvlerëson gjurmën',
  'inbox.group.attribution': 'Atribuimi',
  'inbox.group.trajectory': 'Trajektorja',
  'inbox.group.info': 'Për informim',
  'inbox.task.noData': '{site} — {month} {year} pa asnjë të dhënë',
  'inbox.task.noDataBody': 'Boshllëqet e dobësojnë inventarin dhe e bllokojnë auditimin. Filloni me djegjen stacionare të Scope 1.',
  'inbox.task.addData': 'Shto të dhëna',
  'inbox.task.missingScope': '{site} — {month} {year} i mungon {scope}',
  'inbox.task.oneScope': 'një Scope',
  'inbox.task.missingS2Body': 'Karburanti është i regjistruar, por energjia e blerë jo, prandaj ky muaj e nënvlerëson gjurmën.',
  'inbox.task.missingS1Body': 'Energjia e blerë është e regjistruar, por djegja direkte jo.',
  'inbox.task.oneScopeBody': 'Vetëm njëri nga Scope 1 / Scope 2 është i regjistruar për këtë muaj.',
  'inbox.task.complete': 'Plotëso',
  'inbox.task.unassigned': '{count} regjistrime nuk janë të lidhura me një ndërtesë',
  'inbox.task.unassignedBody': 'Këto përjashtohen nga intensiteti për ndërtesë dhe nga rrjeti i mbulueshmërisë.',
  'inbox.task.reviewRecords': 'Shiko regjistrimet',
  'inbox.task.offTrack': 'Jashtë trajektores kundrejt objektivit {year}',
  'inbox.task.offTrackBody': '{pct}% kundrejt {baseline}, ndërsa kërkohet {required}% në këtë pikë.',
  'inbox.task.openExplorer': 'Hap eksploruesin',
  'inbox.task.biogenic': '{value} tCO₂ biogjenik raportohet veçmas',
  'inbox.task.biogenicBody': 'Biomasë druri dhe peleti — saktësisht e përjashtuar nga totali i Scope 1. Asgjë për të bërë.',

  /* ── entry stepper ─────────────────────────────────────────────────── */
  'entry.step1': 'Zgjidh vendin',
  'entry.step2': 'Shto të dhënat',
  'entry.step3': 'Rishiko & regjistro',
  'entry.scopeNote.1': 'Emetimet direkte nga burimet që kompania i zotëron ose i kontrollon.',
  'entry.scopeNote.2': 'Emetimet indirekte nga energjia që kompania blen dhe konsumon.',
  'entry.scopeNote.3': 'Emetimet indirekte në zinxhirin e vlerës, jashtë kontrollit të drejtpërdrejtë.',
  'entry.card1': '1 · Ku i takojnë këto të dhëna?',
  'entry.card2': "2 · Si dëshironi t'i shtoni?",
  'entry.card2SubOcr': 'Skanimi është më i shpejti dhe e mban dokumentin burimor të bashkëngjitur për auditim.',
  'entry.card2SubNoOcr': 'Bashkëngjitni dokumentin burimor — kjo është prova që kërkon auditimi.',
  'entry.sourceDoc': 'Dokumenti i skanuar',
  'docprev.loading': 'Duke e ngarkuar dokumentin…',
  'docprev.alt': 'Pamja e dokumentit burimor',
  'entry.card3': '3 · Rishiko dhe regjistro',
  'entry.card3Sub': 'Konfirmoni vlerat para se të hyjnë në inventar.',
  'entry.card3Saved': '3 · Aktiviteti u regjistrua',
  'entry.card3SavedSub': 'Regjistrimi është pjesë e inventarit të muajit.',
  'entry.saved': 'E ruajtur',
  'entry.change': 'Ndrysho',
  'entry.continue': 'Vazhdo',
  'entry.back': 'Kthehu',
  'entry.backFix': 'Kthehu & korrigjo',
  'entry.post': 'Regjistro në inventar',
  'entry.posting': 'Duke ruajtur…',
  'entry.addAnother': 'Shto aktivitet të ri',
  'entry.finish': 'Mbaroj',
  'entry.building': 'Ndërtesa',
  'entry.selectBuilding': '-- Zgjidhni ndërtesën --',
  'entry.buildingHint': 'Çdo regjistrim duhet të lidhet me një ndërtesë që intensiteti të mbetet i saktë.',
  'entry.purpose': 'Qëllimi i aktivitetit',
  'entry.purposeUnset': '-- Pa specifikuar --',
  'entry.purposeHint': 'Përdoret për ta ndarë Scope {scope} sipas përdorimit në raporte.',
  'entry.period': 'Periudha',
  'entry.mode.scan': 'Skano dokumentin',
  'entry.mode.manual': 'Shkruaj manualisht',
  'entry.mode.csv': 'Importo CSV',
  'entry.method': 'Metoda',
  'entry.method.scan': 'Dokument i bashkëngjitur',
  'entry.method.manual': 'Shkrim manual',
  'entry.method.csv': 'Import CSV',
  'entry.document': 'Dokumenti',
  'entry.notAttached': 'I pabashkëngjitur',
  'entry.drop': 'Lëshoni {doc} këtu',
  'entry.dropHint': 'PDF, PNG ose JPG deri në 10 MB · ose',
  'entry.browse': 'Zgjidh nga kompjuteri',
  'entry.browseOther': 'Zgjidh dokument tjetër',
  'entry.ocrOn': 'Lexojmë furnizuesin, njehsorët dhe periudhën e faturimit automatikisht, pastaj ju kërkojmë të konfirmoni çdo vlerë nën 90% siguri.',
  'entry.ocrOffTitle': 'Leximi automatik për këtë lloj dokumenti nuk është aktivizuar ende.',
  'entry.ocrOffBody': 'Dokumenti bashkëngjitet si provë dhe mbetet i lidhur me regjistrimin — vlerat shkruani i më poshtë.',
  'entry.csvOffTitle': 'Importimi nga CSV nuk është aktivizuar ende.',
  'entry.csvOffBody': 'Deri atëherë përdorni "Shkruaj manualisht" ose bashkëngjitni dokumentin burimor.',
  'entry.csvDrop': 'skedarin CSV',
  'entry.csvHint': 'Një rresht për çdo lexim · CSV ose XLSX',
  'entry.calc': 'Kalkulo',
  'entry.calculating': 'Duke llogaritur…',
  'entry.emissions': 'Emetimet e llogaritura',
  'entry.coverage': 'Mbulueshmëria e muajit {period}',
  'entry.scopeComplete': 'Ky Scope është i plotë për këtë muaj.',
  'entry.continueTo': 'Vazhdo te {label}',
  'entry.viewRecords': 'Shiko regjistrimet',
  'entry.uploadOk': 'Dokumenti u bashkëngjit dhe u ruajt si provë.',
  'entry.uploadTooBig': 'Dokumenti është mbi 10 MB.',
  'entry.uploadNoBuilding': 'Zgjidhni ndërtesën para se të bashkëngjitni dokumentin.',
  'entry.uploadFailed': 'Gabim gjatë ngarkimit: {message}',

  /* ── category context (right column) ───────────────────────────────── */
  'ctx.latest': 'Të fundit për këtë kategori',
  'ctx.latestSub': '12 muajt e fundit · të gjitha ndërtesat',
  'ctx.noHistory': 'Ende asnjë histori',
  'ctx.noHistoryBody': 'Asnjë regjistrim për {category} në 12 muajt e fundit. I pari që regjistroni e nis trendin.',
  'ctx.loadFail': 'Nuk u lexuan të dhënat',
  'ctx.loadFailBody': 'Rifreskoni faqen për ta provuar përsëri.',
  'ctx.missing': 'Periudhat që mungojnë',
  'ctx.missingSub': 'Boshllëqet që dobësojnë inventarin e këtij viti',
  'ctx.noGaps': 'Pa boshllëqe — çdo ndërtesë është e mbuluar.',
  'ctx.noFacilities': 'Asnjë ndërtesë',
  'ctx.noFacilitiesBody': 'Shtoni një ndërtesë para se të regjistroni të dhëna.',
  'ctx.monthsNoData': '{count} muaj pa të dhëna',
  'ctx.add': 'Shto',

  /* ── entry screens ─────────────────────────────────────────────────── */
  'stationary.title': 'Djegja stacionare',
  'stationary.desc': 'Karburanti i djegur në kaldaja, furra dhe gjeneratorë që kompania i zotëron ose i kontrollon.',
  'stationary.fuel': 'Karburanti',
  'stationary.fuelPlaceholder': 'Lloji i karburantit',
  'stationary.periodType': 'Periudha',
  'stationary.startMonth': 'Muaji fillestar',
  'stationary.quantity': 'Sasia',
  'stationary.doc': 'faturën e karburantit',
  'stationary.biogenicTitle': 'CO₂ biogjenik.',
  'stationary.biogenicBody': 'Raportohet për transparencë, nuk përfshihet në totalin e Scope 1.',
  'stationary.biogenicNote': 'CO₂ biogjenik (dru/pelet) — raportohet veçmas, nuk hyn në totalin e Scope 1.',
  'stationary.ledgerEmpty': 'Asnjë karburant i regjistruar për këtë muaj — kalkulo dhe ruaj të parin.',
  'stationary.success': '{value} t CO₂e u shtua te Scope 1 · Djegia stacionare · {period}.',

  'mobile.title': 'Djegja mobile',
  'mobile.desc': 'Karburanti i djegur në automjetet që kompania i zotëron ose i merr me qira.',
  'mobile.vehicleType': 'Lloji i automjetit',
  'mobile.vehicleId': 'ID e automjetit',
  'mobile.vehicleIdPlaceholder': 'p.sh. AA-123-BB ose emri i automjetit',
  'mobile.registered': 'Të regjistruara: {list}',
  'mobile.category': 'Kategoria e automjetit',
  'mobile.vehicle': 'Automjeti',
  'mobile.method': 'Metoda e regjistrimit',
  'mobile.methodHint': 'Distanca përdoret kur nuk keni faturë karburanti.',
  'mobile.byFuel': 'Sipas karburantit',
  'mobile.byDistance': 'Sipas distancës',
  'mobile.fuelType': 'Lloji i karburantit',
  'mobile.selectFuel': 'Zgjidhni karburantin',
  'mobile.distanceType': 'Lloji i automjetit (për distancë)',
  'mobile.distanceMethod': 'Distanca e kaluar',
  'mobile.month': 'Muaji',
  'mobile.evNote': 'Automjetet elektrike nuk kanë emetime direkte (Scope 1). Konsumi i energjisë raportohet në Scope 2.',
  'mobile.evAction': 'Regjistro konsumin (Scope 2)',
  'mobile.hybridNote': 'Për automjetet hibride regjistroni karburantin këtu (Scope 1); energjia e karikimit regjistrohet veçmas në Scope 2.',
  'mobile.hybridPrompt': 'Dëshironi të regjistroni energjinë e karikimit për këtë automjet hibrid?',
  'mobile.hybridAction': 'Regjistro karikimin (Scope 2)',
  'mobile.later': 'Më vonë',
  'mobile.combined': 'Totali i kombinuar — hibrid',
  'mobile.combinedFuel': 'Pjesa e karburantit (Scope 1)',
  'mobile.combinedCharge': 'Pjesa e karikimit (Scope 2)',
  'mobile.combinedTotal': 'Totali i kombinuar',
  'mobile.noCharging': 'Nuk ka regjistrim për karikimin elektrik. Shtoni një regjistrim Scope 2 (hybrid_charging) për të njëjtën ndërtesë dhe periudhë që të hyjë në total.',
  'mobile.ledgerEmpty': 'Asnjë automjet i regjistruar për këtë muaj — kalkulo dhe ruaj të parin.',
  'mobile.success': '{value} t CO₂e u shtua te Scope 1 · Djegia mobile · {period}.',

  /* ── records ───────────────────────────────────────────────────────── */
  'rec.title': 'Regjistrimet',
  'rec.desc': 'Çdo regjistrim në inventar, me gjurmën e llogaritjes pas secilës shifër.',
  'rec.loading': 'Duke u ngarkuar…',
  'rec.search': 'Kërko dokument, kategori ose ndërtesë…',
  'rec.facility': 'Ndërtesa',
  'rec.period': 'Periudha',
  'rec.sourceDoc': 'Dokumenti burimor',
  'rec.record': 'Regjistrimi',
  'rec.result': 'Rezultati',
  'rec.trace': 'Gjurma e llogaritjes',
  'rec.noMatch': 'Asnjë regjistrim nuk përputhet me këta filtra',
  'rec.noMatchBody': 'Provoni të pastroni kërkimin ose të zgjeroni filtrat e ndërtesës dhe burimit.',

  /* ── data quality ──────────────────────────────────────────────────── */
  'dq.title': 'Cilësia e të dhënave',
  'dq.desc': 'Katër masa të veçanta, jo një notë e vetme — çdo gjetje emërton shkakun e vet.',
  'dq.loading': 'Duke u ngarkuar…',
  'dq.verified': 'Të verifikuara',
  'dq.evidenced': 'Me provë',
  'dq.completeness': 'Plotësia',
  'dq.findings': 'Gjetje të hapura',
  'dq.findingsTitle': 'Gjetjet',
  'dq.findingsSub': 'Renditur sipas ndikimit në totalin e raportuar',
  'dq.nothing': 'Asgjë për të zgjidhur',
  'dq.nothingBody': 'Çdo regjistrim rakordon dhe çdo muaj-ndërtesë ka të dhëna.',
  'dq.evidenceBasis': 'Baza e provave',
  'dq.evidenceBasisSub': 'Pjesa e gjurmës sipas atij që qëndron pas saj',
  'dq.coverageFacility': 'Mbulueshmëria sipas ndërtesës',
  'dq.coverageMatrix': 'Matrica e mbulueshmërisë',
  'dq.noFacilities': 'Asnjë ndërtesë',
  'dq.noFacilitiesBody': 'Shtoni një ndërtesë për të ndjekur mbulueshmërinë.',

  /* ── explorer ──────────────────────────────────────────────────────── */
  'exp.title': 'Eksploruesi i emetimeve',
  'exp.desc': 'Zbërtheni gjurmën nga Scope-i deri te karburanti ose aktiviteti individual.',
  'exp.total': 'Totali',
  'exp.totalSub': 'Në të gjitha Scope-et në këtë përzgjedhje',
  'exp.byScope': 'Sipas Scope-it',
  'exp.byScopeSub': 'Pjesa e gjurmës së përzgjedhur',
  'exp.breakdown': 'Zbërthimi',
  'exp.breakdownSub': 'Hapni një grup për të shohur karburantet dhe aktivitetet individuale pas tij',
  'exp.largest': 'Burimet më të mëdha',
  'exp.trend': 'Trendi mujor',
  'exp.trendSub': '6 muajt e fundit · shenja e zezë tregon objektivin −10% vit-për-vit për atë muaj',
  'exp.noEmissions': 'Asnjë emetim në këtë përzgjedhje',
  'exp.noEmissionsBody': 'Provoni një periudhë më të gjerë, ose një ndërtesë tjetër.',
  'exp.noMonthly': 'Pa të dhëna mujore',
  'exp.nothingToRank': 'Asgjë për të renditur',
  'exp.s1stationary': 'Scope 1 — djegja stacionare',
  'exp.s1mobile': 'Scope 1 — djegja mobile',
  'exp.s1process': 'Scope 1 — emetimet nga proceset',
  'exp.s2': 'Scope 2 — energjia e blerë',
  'exp.biogenic': 'CO₂ biogjenik — memo, i përjashtuar nga totalet',

  /* ── facilities ────────────────────────────────────────────────────── */
  'fac.title': 'Ndërtesat',
  'fac.desc': 'Ndërtesat përcaktojnë kufirin e raportimit.',
  'fac.sub': 'Ndërtesat përcaktojnë kufirin e raportimit',
  'fac.empty': 'Ende asnjë ndërtesë',
  'fac.emptyBody': 'Shtoni objektet brenda kufirit tuaj të raportimit — çdo regjistrim emetimi lidhet me një.',
  'fac.profile': 'Profili i ndërtesës',
  'fac.inBoundary': 'Ndërtesa në kufi',
  'fac.totalArea': 'Sipërfaqja totale',
  'fac.avgIntensity': 'Intensiteti mesatar',
  'fac.intensity': 'Intensiteti',
  'fac.coverage': 'Mbulueshmëria e të dhënave',
  'fac.total': 'Totali',

  /* ── account ───────────────────────────────────────────────────────── */
  'acc.title': 'Llogaria',
  'acc.desc': 'Profili, plani dhe fjalëkalimi juaj.',
  'acc.details': 'Detajet e llogarisë',
  'acc.detailsSub': 'Përdoret në kopertinën e raportit GHG dhe në eksportime.',
  'acc.identity': 'Identiteti',
  'acc.identitySub': 'Caktuar kur u krijua llogaria — kontaktoni mbështetjen për ta ndryshuar emailin.',
  'acc.companySize': 'Madhësia e kompanisë',
  'acc.password': 'Fjalëkalimi',
  'acc.currentPassword': 'Fjalëkalimi aktual',
  'acc.newPassword': 'Fjalëkalimi i re',
  'acc.confirmPassword': 'Konfirmo fjalëkalimin e re',

  /* ── scope 2 electricity ───────────────────────────────────────────── */
  'elec.title': 'Fatura e rrymës',
  'elec.desc': 'Energjia elektrike e blerë nga rrjeti dhe e konsumuar — metoda e bazuar në vendndodhje.',
  'elec.doc': 'faturën e rrymës',
  'elec.purpose': 'Qëllimi i konsumit',
  'elec.purposeBuilding': 'Elektriciteti i ndërtesës',
  'elec.purposeEv': 'Karikimi i automjeteve elektrike',
  'elec.purposeHybrid': 'Karikimi i automjeteve hibride',
  'elec.usage': 'Përdorimi',
  'elec.vehicle': 'Automjeti',
  'elec.selectVehicle': '-- Zgjidhni automjetin --',
  'elec.consumption': 'Konsumi',
  'elec.billingPeriod': 'Periudha e faturës',
  'elec.totalConsumption': 'Konsumi total',
  'elec.register': 'Regjistri {name}',
  'elec.sourceDoc': 'Dokumenti burimor',
  'elec.ledgerEmpty': 'Asnjë faturë e regjistruar për këtë muaj — skano ose shkruaj të parën.',
  'elec.success': '{value} t CO₂e u shtua te Scope 2 · Elektriciteti · {period}.',
  'elec.errBuilding': 'Ju lutem zgjidhni një ndërtesë para se të ruani të dhënat',
  'elec.errPeriod': 'Ju lutem vendosni periudhën e faturës',
  'elec.errCalc': 'Ju lutem kalkuloni faturën para se ta ruani',
  'elec.processing': 'Duke procesuar faturat…',
  'elec.progress': '{current} nga {total}',
  'elec.reading': 'Duke lexuar dokumentin…',
  'elec.bulkUpload': 'Ngarko disa fatura',
  'elec.bulkOnlyBuilding': 'Ngarkimi në grup vlen vetëm për elektricitetin e ndërtesës.',
  'elec.pickBuildingFirst': 'Ju lutem zgjidhni ndërtesën para se të ngarkoni faturën',
  'elec.ocrFailTitle': 'Fatura nuk u lexua',
  'elec.ocrFailBody': 'Ju lutem ngarkoni një fotografi më të qartë, ose shkruani vlerat manualisht.',
  'elec.ocrFailManual': 'Kalkulo manualisht',
  'elec.ocrFailRetry': 'Ngarko faturë tjetër',

  /* ── scope 1 process ───────────────────────────────────────────────── */
  'proc.title': 'Emetimet nga proceset',
  'proc.desc': 'Emetime të lëshuara nga një proces fizik ose kimik, jo nga djegia e karburantit.',
  'proc.applicableQ': 'A kryen kompania juaj procese industriale që gjenerojnë drejtpërdrejt emetime CO₂ (përveç djegies së karburanteve)?',
  'proc.applicableHint': 'P.sh. prodhimi i çimentos/klinkerit, gëlqeres, qelqit, qeramikës, çelikut, aluminit, ose përdorimi i sodës kalcinuese / gurit gëlqeror / dolomitit.',
  'proc.yes': 'Po, kryejmë procese industriale',
  'proc.no': 'Asnjë nga si më sipër',
  'proc.notIdentified': 'Nuk janë identifikuar emetime nga proceset',
  'proc.notIdentifiedBody': 'Ky seksion mbetet bosh për këtë kompani. Mund ta ndryshoni përgjigjen në çdo kohë.',
  'proc.changeAnswer': 'Ndrysho përgjigjen',
  'proc.type': 'Lloji i procesit',
  'proc.selectType': '-- Zgjidhni procesin --',
  'proc.doc': 'raportin e prodhimit',
  'proc.ledgerEmpty': 'Asnjë proces i regjistruar për këtë muaj — kalkulo dhe ruaj të parin.',
  'proc.success': '{value} t CO₂e u shtua te Scope 1 · Proceset · {period}.',

  /* ── scope 2 district heat & cooling ───────────────────────────────── */
  'heat.title': 'Ngrohje & ftohje',
  'heat.desc': 'Nxehtësi ose ujë i ftohtë i blerë nga një rrjet i jashtëm.',
  'heat.type': 'Lloji',
  'heat.selectType': '-- Zgjidhni llojin --',
  'heat.fuel': 'Burimi i energjisë',
  'heat.selectFuel': '-- Zgjidhni burimin --',
  'heat.doc': 'faturën e ngrohjes',
  'heat.ledgerEmpty': 'Asnjë regjistrim për këtë muaj — kalkulo dhe ruaj të parin.',
  'heat.success': '{value} t CO₂e u shtua te Scope 2 · Ngrohje & ftohje · {period}.',

  /* ── scope 3 ───────────────────────────────────────────────────────── */
  's3.title': 'Udhëtime & komutim',
  's3.desc': 'Emetimet e zinxhirit të vlerës — udhëtimet e biznesit dhe udhëtimet e punëtorëve nga shtëpia në punë.',
  's3.category': 'Kategoria',
  's3.selectCategory': '-- Zgjidhni kategorinë --',
  's3.activity': 'Aktiviteti',
  's3.selectActivity': '-- Zgjidhni aktivitetin --',
  's3.notReady': 'Kjo kategori nuk është aktivizuar ende — nuk ka faktorë emetimi për të.',
  's3.employees': 'Punëtorë gjithsej',
  's3.workDays': 'Ditë pune / muaj',
  's3.survey': 'Anketa e komutimit',
  's3.surveySub': 'Ndani punëtorët sipas mënyrës së udhëtimit — përqindjet duhet të mbledhin 100%.',
  's3.mode': 'Mënyra',
  's3.share': 'Përqindja',
  's3.avgDistance': 'Distanca mesatare (km, vajtje-ardhje)',
  's3.allocated': 'Të alokuara: {pct}%',
  's3.overAllocated': 'Përqindjet kalojnë 100% — rregullojini para se të ruani.',
  's3.underAllocated': 'Përqindjet nuk arrijnë 100%. Regjistrimi lejohet, por mbulon vetëm {pct}% të punëtorëve.',
  's3.commuting': 'Udhëtimet e punëtorëve',
  's3.doc': 'dokumentin mbështetës',
  's3.ledgerEmpty': 'Asnjë regjistrim Scope 3 për këtë muaj — kalkulo dhe ruaj të parin.',
  's3.success': '{value} t CO₂e u shtua te Scope 3 · {period}.',

  /* ── reports ───────────────────────────────────────────────────────── */
  'reports.title': 'Raportet',
  'reports.desc': 'Inventari i GHG që qëndron pas raportit tuaj PDF — i njëjti asembler, të njëjtat shifra, kështu që ajo që eksportoni është ajo që shihni këtu.',
  'reports.loading': 'Duke përgatitur inventarin e GHG…',
  'reports.loadFail': 'Raporti nuk u ngarkua',
  'reports.loadFailBody': 'Endpoint-i i panelit nuk u përgjigj. Të dhënat tuaja nuk janë prekur.',
  'reports.uploadLogo': 'Ngarko logon',
  'reports.changeLogo': 'Ndrysho logon',
  'reports.removeLogo': 'Hiq logon',
  'reports.logoTitle': 'Logoja shfaqet në kopertinën e raportit PDF',
  'reports.exportCsv': 'Eksporto CSV',
  'reports.exportPdf': 'Eksporto PDF',
  'reports.exporting': 'Duke eksportuar…',
  'reports.timeRange': 'Periudha',
  'reports.range.lastMonth': 'Muaji i fundit',
  'reports.range.last3': '3 muajt e fundit',
  'reports.range.last6': '6 muajt e fundit',
  'reports.range.lastYear': 'Viti i fundit',
  'reports.range.allTime': 'E gjithë koha',
  'reports.scope': 'Scope',
  'reports.facility': 'Ndërtesa',
  'reports.filtered': 'Filtruar te {scope}',
  'reports.breakdown': 'Ndarja e emetimeve',
  'reports.breakdownSub': 'Totali mujor kundrejt objektivit −10% vit-për-vit (shenja e zezë)',
  'reports.nothingToPlot': 'Asgjë për të vizatuar',
  'reports.nothingToPlotBody': 'Asnjë regjistrim nuk hyn në këtë filtër.',
  'reports.byScope': 'Emetimet sipas Scope-it',
  'reports.byScopeSub': 'Kategoritë e GHG Protocol',
  'reports.noEmissions': 'Asnjë emetim i regjistruar',
  'reports.noEmissionsBody': 'Shtoni të dhëna për të parë ndarjen sipas Scope-it.',
  'reports.most': 'Emetimet më të mëdha',
  'reports.mostSub': 'Burimet më të mëdha në periudhën e zgjedhur',
  'reports.noSources': 'Asnjë burim emetimi',
  'reports.noSourcesBody': 'Asgjë e regjistruar për këtë Scope në këtë periudhë.',
  'reports.scope1': 'Scope 1 — emetimet direkte',
  'reports.scope2': 'Scope 2 — energjia e blerë',
  'reports.scope3': 'Scope 3 — zinxhiri i vlerës',
  'reports.scope3Sub': 'Udhëtime biznesore dhe komutim · 2 nga 15 kategori',
  'reports.stackedBy': 'Të grumbulluara sipas aktivitetit · totalet në tCO₂e',
  'reports.vsLastYear': '{sign}{value} tCO₂e kundrejt vitit të kaluar',
  'reports.last8': '8 muajt e fundit',
  'reports.last12': '12 muajt e fundit',
  'reports.allTime': 'E gjithë koha',
  'reports.biogenic': 'CO₂ biogjenik',
  'reports.biogenicSub': 'Raportohet paralelisht — i përjashtuar nga totali i Scope 1, sipas GHG Protocol & ISO 14064',
  'reports.parallel': 'Raportim paralel',
  'reports.fuel': 'Karburanti',
  'reports.records': 'Regjistrime',
  'reports.quantity': 'Sasia',
  'reports.kpi.inventory': 'Inventari GHG {year} (totali i raportit)',
  'reports.kpi.evidence': 'Provat në dosje — {year}',
  'reports.kpi.evidenceUnit': 'regjistrime',
  'reports.kpi.evidenceFoot': '{count} {facilities} · shfaqen në raportin PDF',
  'reports.kpi.firstYear': 'Viti i parë i inventarit — pa të dhëna të {year}',
  'reports.kpi.vsBaseline': 'kundrejt bazës {year}',
  'reports.kpi.scopeSplit': 'Ndarja sipas Scope-it',
  'reports.kpi.scopeSplitFoot': 'Pjesa e Scope 1 / 2 / 3 në total',
  'reports.kpi.biogenic': 'CO₂ biogjenik',
  'reports.kpi.biogenicFoot': 'Raportohet paralelisht, jo në Scope 1',
  'reports.bannerMissing': 'Muajit {period} i mungojnë të dhënat: {missing}.',
  'reports.bannerAdd': 'Shto të dhënat',
  'reports.bannerDismiss': 'Mbyll',
  'reports.biogenicSeparate': 'Biogjenike {value} tCO₂e raportohet veçmas — shih seksionin CO₂ biogjenik.',

  /* ── CBAM ──────────────────────────────────────────────────────────── */
  // The overview is an analysis surface whose artefacts go to EU importers and
  // verifiers, so its strings stay English in both dictionaries (the "artefacts
  // that leave the building are English" decision). Nav labels, entry screens
  // and the upsell are translated as usual.
  'cbam.overview.title': 'CBAM overview',
  'cbam.overview.desc': 'Embedded emissions per exported good, completeness checks and the indicative certificate cost curve 2026–2034.',
  'cbam.installations.title': 'Instalimet',
  'cbam.installations.desc': 'Instalimet prodhuese jashtë BE-së — vendndodhja, operatori dhe çmimi i karbonit për çdo vit raportues.',
  'cbam.goods.title': 'Mallrat & prekursorët',
  'cbam.goods.desc': 'Mallrat CBAM që eksportoni (kodi CN, tonazhi i prodhimit) dhe prekursorët e tyre, me vlera furnizuesi ose default.',
  'cbam.gate.title': 'Moduli CBAM kërkon planin Compliance & Export',
  'cbam.gate.body': 'Llogaritni emetimet e përfshira (SEE) të mallrave që eksportoni në BE, përgatitni raportin e emetimeve të operatorit dhe vlerësoni koston e certifikatave CBAM deri më 2034.',
  'cbam.gate.cta': 'Kalo në Compliance & Export',
  'cbam.gate.info': 'Nga 1 janari 2026 CBAM është në periudhën definitive: si operator i një instalimi eksportues, ju kërkohet me ligj të monitoroni dhe llogaritni emetimet e përfshira dhe t\'i verifikoni ato (Rregullorja (BE) 2023/956; Rregullorja zbatuese (BE) 2025/2547). Deklarata e parë për importet e 2026-ës dorëzohet deri më 30 shtator 2027; shitja e certifikatave nis më 1 shkurt 2027.',
  'cbam.form.cancel': 'Anulo',

  // The overview screen itself — English in BOTH dictionaries, like the
  // cbam.overview.* page chrome and the CN-lookup read-out above.
  'cbam.ov.exportSee': 'Skedari SEE i KE-së',
  'cbam.ov.exportedSee': 'Skedari SEE i Komisionit Evropian u shkarkua — hapeni në Excel (fletët përmbledhëse llogariten vetë).',
  'cbam.ov.exportSeeHint': 'Shablloni zyrtar i Komisionit Evropian për komunikimin CBAM, i plotësuar me të dhënat e këtij instalimi',
  'cbam.ov.export': 'Export workbook (.xlsx)',
  'cbam.ov.exporting': 'Exporting…',
  'cbam.ov.exportHint': 'Register an installation first — the workbook is generated per installation.',
  'cbam.ov.exported': 'Workbook downloaded',
  'cbam.ov.exportFailed': 'Download failed. Try again.',
  'cbam.ov.kpiGoods': 'Goods covered',
  'cbam.ov.kpiProduction': 'Total production',
  'cbam.ov.kpiSee': 'Weighted SEE (direct)',
  'cbam.ov.kpiCost': 'Indicative cost {year}',
  'cbam.ov.kpiCostFoot': 'at {price} €/tCO₂e — EU ETS assumption',
  'cbam.ov.goodsTitle': 'Embedded emissions per good',
  'cbam.ov.goodsSub': 'Click a row for the full calculation trace.',
  'cbam.ov.indirectMemo': 'reported, not charged',
  'cbam.ov.deMinimis': 'Under 50 t exported to the EU. An importer whose cumulative CBAM-goods imports stay at or below 50 t in the calendar year is exempt (Art 2a) — the exemption belongs to the importer, never to the operator, and splitting consignments counts as circumvention.',
  'cbam.ov.empty': 'No installations yet',
  'cbam.ov.emptyBody': 'Register your production installation, add its processes and goods — the overview computes embedded emissions and certificate costs from there.',
  'cbam.ov.emptyCta': 'Go to installations',
  'cbam.ov.noGoods': 'No goods for {year}',
  'cbam.ov.noGoodsBody': 'Add the goods you exported in this reporting year — each CN code brings its sector, default value and benchmark.',
  'cbam.ov.noGoodsCta': 'Go to goods & precursors',
  'cbam.ov.costTitle': 'Indicative certificate cost 2026–2034',
  'cbam.ov.costSub': 'max(0, chargeable SEE × EU export − free allocation) × ETS price, phased in by year.',
  'cbam.ov.etsAssumption': 'indicative — EU ETS price assumption',
  'cbam.ov.etsExportsNote': 'The workbook and trace-PDF exports use this price for their indicative cost columns.',
  'cbam.ov.etsInvalid': 'The assumption must be above 0 €/t — the server default applies until then.',
  'cbam.ov.costEmpty': 'The cost curve appears once a good has production and EU-export tonnage.',
  'cbam.ov.timeline': 'Certificate sales start 1 February 2027; the first declaration, covering 2026 imports, is due by 30 September 2027. No certificates are bought during 2026.',
  'cbam.ov.completeTitle': 'Completeness',
  'cbam.ov.completeSub': 'What the operator\'s emissions report still needs.',
  'cbam.ov.completeAllOk': 'Everything the report needs is in place.',
  'cbam.ov.fix': 'Fix',
  'cbam.ov.defaultsTitle': 'Defaults cost you money.',
  'cbam.ov.defaultsBody': 'Default values carry a mark-up (+10% in 2026, +20% in 2027, +30% from 2028) on top of conservative country averages — actual data usually beats them. Ask your suppliers for verified embedded-emissions values, or book verification for your own process.',
  'cbam.ov.defaultsDelta': '{cn}: {delta} tCO₂e/t of its SEE rides on default values — up to {eur} € of the {year} cost.',
  'cbam.ov.defaultsDeltaNoEur': '{cn}: {delta} tCO₂e/t of its SEE rides on default values.',
  'cbam.ov.seeTitle': 'Specific embedded emissions',
  'cbam.ov.traceTitle': 'Calculation trace',
  'cbam.ov.traceEmpty': 'The server returned no trace for this good.',
  'cbam.ov.precTitle': 'Precursors',
  'cbam.ov.precEmpty': 'No precursors recorded — only the installation\'s own process emissions are embedded.',
  'cbam.ov.boundary': 'System boundary for aluminium products: fuel combustion and flue-gas treatment only — cutting, welding and finishing stay outside. If those are your only emission sources, a near-zero own-process SEE is legitimate.',
  'cbam.ov.tracePdf': 'Download trace PDF',
  'cbam.ov.close': 'Close',

  /* — installations: list + wizard (entry surface → Albanian) — */
  'cbam.inst.new': 'Instalim i ri',
  'cbam.inst.backToList': 'Kthehu te lista',
  'cbam.inst.empty': 'Ende pa instalime',
  'cbam.inst.emptyBody': 'Regjistroni instalimin tuaj prodhues — vendndodhjen, operatorin dhe vitin raportues — dhe më pas shtoni proceset dhe mallrat.',
  'cbam.inst.step1': 'Zgjidh vendndodhjen',
  'cbam.inst.step2': 'Detajet e instalimit',
  'cbam.inst.step3': 'Viti raportues',
  'cbam.inst.card1': 'Ku ndodhet instalimi?',
  'cbam.inst.card1Sub': 'Lidheni me një ndërtesë ekzistuese — emetimet e regjistruara aty ushqejnë llogaritjen SEE.',
  'cbam.inst.building': 'Ndërtesa',
  'cbam.inst.selectBuilding': 'Zgjidh ndërtesën',
  'cbam.inst.standalone': 'Instalim i veçantë — shkruaj adresën manualisht',
  'cbam.inst.standaloneShort': 'Instalim i veçantë',
  'cbam.inst.buildingHint': 'Zgjedhja e ndërtesës e plotëson shtetin automatikisht.',
  'cbam.inst.country': 'Shteti',
  'cbam.inst.countryPh': 'p.sh. Bosnjë e Hercegovinë',
  'cbam.inst.city': 'Qyteti',
  'cbam.inst.address': 'Adresa',
  'cbam.inst.card2': 'Detajet e instalimit',
  'cbam.inst.card2Sub': 'Si paraqitet instalimi në regjistrin CBAM dhe te importuesit tuaj.',
  'cbam.inst.name': 'Emri i instalimit',
  'cbam.inst.unlocode': 'UN/LOCODE',
  'cbam.inst.unlocodeHint': 'Kodi i vendndodhjes i OKB-së (p.sh. BA SJJ) — opsional, por regjistri CBAM e kërkon për identifikim.',
  'cbam.inst.operatorName': 'Emri i operatorit',
  'cbam.inst.operatorEmail': 'Email-i i operatorit',
  'cbam.inst.registryId': 'ID e regjistrit CBAM',
  'cbam.inst.registryIdHint': 'ID-ja e instalimit pasi regjistroheni në regjistrin CBAM — mund të plotësohet edhe më vonë.',
  'cbam.inst.o3ci': 'I regjistruar në modulin O3CI',
  'cbam.inst.o3ciCallout': 'Moduli O3CI i regjistrit CBAM i lejon operatorit jashtë BE-së të ngarkojë planin e monitorimit dhe raportin e emetimeve VETËM NJË HERË dhe t\'i ndajë me çdo importues në BE — në vend që t\'ia dërgojë secilit me email. Regjistrimi është vullnetar, por rekomandohet fuqishëm; ruajeni ID-në e regjistrit këtu sapo ta merrni.',
  'cbam.inst.card3': 'Viti raportues',
  'cbam.inst.card3Sub': 'Çmimi i karbonit i paguar në vendin e origjinës dhe statusi i verifikimit.',
  'cbam.inst.year': 'Viti',
  'cbam.inst.carbonPrice': 'Çmimi i karbonit i paguar',
  'cbam.inst.noCarbonPrice': '{country} sot nuk ka çmim karboni — lëreni 0.',
  'cbam.inst.carbonPriceHint': 'Çmimi efektivisht i paguar për tCO₂e në vendin e origjinës, neto nga rimbursimet — zbritet nga detyrimi CBAM.',
  'cbam.inst.instrument': 'Instrumenti',
  'cbam.inst.verification': 'Statusi i verifikimit',
  'cbam.inst.ver.unverified': 'I paverifikuar',
  'cbam.inst.ver.verifier_engaged': 'Verifikuesi i angazhuar',
  'cbam.inst.ver.verified': 'I verifikuar',
  'cbam.inst.verifierName': 'Emri i verifikuesit',
  'cbam.inst.coverageNote': 'Shënim mbi mbulimin',
  'cbam.inst.create': 'Krijo instalimin',
  'cbam.inst.creating': 'Duke krijuar…',
  'cbam.inst.created': 'Instalimi u krijua',
  'cbam.inst.updated': 'Instalimi u përditësua',
  'cbam.inst.deleted': 'Instalimi u fshi',
  'cbam.inst.saveFailed': 'Nuk u ruajt. Provoni përsëri.',
  'cbam.inst.saveCta': 'Ruaj ndryshimet',
  'cbam.inst.yearSaveCta': 'Ruaj',
  'cbam.inst.vName': 'Emri i instalimit është i detyrueshëm.',
  'cbam.inst.confirmDelete': 'Të fshihet {name}? Proceset dhe vitet raportuese fshihen bashkë me të; mallrat që u referohen atyre proceseve mbeten pa proces.',
  'cbam.inst.edit': 'Ndrysho instalimin',
  'cbam.inst.addYear': 'Shto vit raportues',
  'cbam.inst.editYear': 'Viti raportues {year}',
  'cbam.inst.yearSaved': 'Viti raportues u ruajt',
  'cbam.inst.contextTitle': 'Si funksionon',
  'cbam.inst.contextBody': 'Instalimi është njësia që regjistri CBAM njeh: vendndodhja fizike ku prodhohen mallrat. Çdo instalim ka procese prodhimi dhe çdo mall i eksportuar i takon një procesi. Emetimet e regjistruara në Karbon360 për ndërtesën e lidhur ndahen mes proceseve me përqindjet e atribuimit.',

  /* — processes + attribution (entry surface → Albanian) — */
  'cbam.proc.add': 'Shto proces',
  'cbam.proc.new': 'Proces i ri prodhimi',
  'cbam.proc.edit': 'Ndrysho procesin',
  'cbam.proc.name': 'Emri i procesit',
  'cbam.proc.namePh': 'p.sh. Prodhimi i dyerve dhe dritareve',
  'cbam.proc.sector': 'Sektori',
  'cbam.proc.category': 'Kategoria e agreguar e mallrave',
  'cbam.proc.route': 'Rruga e prodhimit',
  'cbam.proc.routeHint': 'Rruga duhet të përputhet me mënyrën reale të prodhimit — vlerat default dhe benchmark-ët ndryshojnë sipas rrugës.',
  'cbam.route.K': 'K — Primar: nga xeherori (p.sh. elektrolizë alumini)',
  'cbam.route.L': 'L — Sekondar: rishkrirje e skrapit',
  'cbam.route.C': 'C — Çelik karboni përmes furrës së lartë (BF–BOF)',
  'cbam.route.D': 'D — Hekur i reduktuar direkt + furrë me hark elektrik (DRI–EAF)',
  'cbam.route.E': 'E — Furrë me hark elektrik, nga skrapi (EAF)',
  'cbam.proc.attr': 'Atribuimi',
  'cbam.proc.attrSub': 'Sa nga inventari i instalimit i takon këtij procesi — lëreni 100/100 nëse është procesi i vetëm.',
  'cbam.proc.attrYear': 'Viti',
  'cbam.proc.directShare': 'Pjesa e emetimeve direkte',
  'cbam.proc.elecShare': 'Pjesa e energjisë elektrike',
  'cbam.proc.inclStationary': 'Përfshi djegien stacionare',
  'cbam.proc.inclMobile': 'Përfshi automjetet',
  'cbam.proc.inclMobileHint': 'Automjetet rrugore jashtë kufirit të instalimit nuk përfshihen — lëreni të çaktivizuar nëse s\'jeni të sigurt.',
  'cbam.proc.inclProcess': 'Përfshi emetimet e procesit',
  'cbam.proc.methodNote': 'Si u nda (metoda)',
  'cbam.proc.methodNotePh': 'p.sh. nën-matje, sipërfaqe, orë pune…',
  'cbam.proc.boundaryAl': 'Kufiri i sistemit për produktet e aluminit: vetëm djegia e karburanteve dhe trajtimi i gazrave të tymit. Prerja, saldimi dhe përfundimi janë JASHTË kufirit — mos ua atribuoni emetimet këtij procesi.',
  'cbam.proc.saved': 'Procesi u ruajt',
  'cbam.proc.deleted': 'Procesi u fshi',
  'cbam.proc.saveCta': 'Ruaj procesin',
  'cbam.proc.vRequired': 'Emri, sektori dhe kategoria e mallrave janë të detyrueshme.',
  'cbam.proc.confirmDelete': 'Të fshihet procesi "{name}"? Mallrat e caktuara aty mbeten pa proces.',

  /* — goods & precursors (entry surface → Albanian) — */
  'cbam.goods.new': 'Shto mall',
  'cbam.goods.empty': 'Ende pa mallra',
  'cbam.goods.emptyBody': 'Shtoni mallrat që eksportoni në BE — kodi CN tregon automatikisht sektorin, vlerën default dhe benchmark-un.',
  'cbam.goods.needProcess': 'Së pari krijoni një instalim me të paktën një proces prodhimi — çdo mall i takon një procesi.',
  'cbam.goods.toInstallations': 'Shko te instalimet',
  'cbam.goods.modalNew': 'Shto mall CBAM',
  'cbam.goods.modalEdit': 'Ndrysho mallin',
  'cbam.goods.process': 'Procesi i prodhimit',
  'cbam.goods.year': 'Viti',
  'cbam.goods.cn': 'Kodi CN',
  'cbam.goods.cnHint': 'Shkruani të paktën 6 shifra — kodi kontrollohet live kundrejt Aneksit I.',
  'cbam.goods.description': 'Përshkrimi',
  'cbam.goods.descriptionPh': 'p.sh. Dritare alumini me dy kanata',
  'cbam.goods.route': 'Rruga e prodhimit',
  'cbam.goods.routeMismatch': 'Vlera default për këtë CN/vend supozon rrugën {route} — nëse rruga juaj ndryshon, defaulti nuk përputhet.',
  'cbam.goods.production': 'Prodhimi vjetor',
  'cbam.goods.euExport': 'Nga kjo, eksport në BE',
  'cbam.goods.alParams': 'Parametrat e sektorit të aluminit',
  'cbam.goods.scrap': 'Skrap për ton produkti',
  'cbam.goods.preConsumer': 'Skrap para-konsumit',
  'cbam.goods.nonAl': 'Elemente jo-alumini',
  'cbam.goods.nonAlHint': 'Plotësohet vetëm nëse kalon 1% të masës.',
  'cbam.goods.precursors': 'Prekursorët',
  'cbam.goods.precursorsSub': 'Materialet hyrëse që janë vetë mallra CBAM (p.sh. alumini i papërpunuar 7601, profilet 7604).',
  'cbam.goods.addPrecursor': 'Shto prekursor',
  'cbam.goods.saved': 'Malli u ruajt',
  'cbam.goods.deleted': 'Malli u fshi',
  'cbam.goods.saveFailed': 'Nuk u ruajt. Provoni përsëri.',
  'cbam.goods.saveCta': 'Ruaj mallin',
  'cbam.goods.vProcess': 'Zgjidhni procesin e prodhimit.',
  'cbam.goods.vCn': 'Kodi CN duhet të ketë të paktën 6 shifra.',
  'cbam.goods.vProduction': 'Prodhimi vjetor është i detyrueshëm.',
  'cbam.goods.vPrec': 'Çdo prekursor kërkon emër dhe sasi (t/t) mbi zero.',
  'cbam.goods.vPrecCountry': 'Zgjidhni vendin e prodhimit për çdo prekursor me vlerë default — ose "Origjinë e panjohur", nëse vendi nuk identifikohet dot.',
  'cbam.goods.vPrecSource': 'Çdo prekursor "Prodhuar në këtë instalim" kërkon një mall burim.',
  'cbam.goods.vPrecSelf': 'Një mall nuk mund të jetë prekursor i vetvetes — zgjidhni një mall tjetër si burim.',
  'cbam.goods.vPrecSameProcess': 'Malli burim duhet t\'i takojë një procesi tjetër prodhimi — brenda të njëjtit proces rrjedha e brendshme nuk mbart term SEE×masë (Aneksi III, pika B, IR (BE) 2025/2547).',
  'cbam.goods.yearClearedSources': 'Ndryshimi i vitit raportues i hoqi lidhjet "Prodhuar në këtë instalim" — një prekursor i brendshëm duhet t\'i referohet një malli të të njëjtit vit raportues (Aneksi II, pika E(1), IR (BE) 2025/2547). Zgjidhni përsëri mallrat burim.',
  'cbam.goods.precSyncFailed': 'Malli u ruajt, por këta prekursorë dështuan: {rows}. Shtypni "Ruaj mallin" përsëri — rreshtat e tjerë janë ruajtur.',
  'cbam.goods.confirmDelete': 'Të fshihet {cn} ({year})? Prekursorët e tij fshihen bashkë me të.',
  'cbam.prec.name': 'Emri',
  'cbam.prec.namePh': 'p.sh. Profile alumini',
  'cbam.prec.cn': 'Kodi CN',
  'cbam.prec.tpt': 'Sasia për ton të mallit',
  'cbam.prec.tptHint': 'Përfshi humbjet — edhe copëzat/skrapi që nuk përfundojnë në mallin final llogariten.',
  'cbam.prec.euOrigin': 'Origjinë BE / ZEE / Zvicër',
  'cbam.prec.euOriginHint': 'Llogaritet me zero emetime të përfshira.',
  'cbam.prec.supplier': 'Vlerë furnizuesi',
  'cbam.prec.default': 'Vlerë default',
  'cbam.prec.seeDirect': 'SEE direkte',
  'cbam.prec.seeIndirect': 'SEE indirekte',
  'cbam.prec.supplierName': 'Furnizuesi',
  'cbam.prec.supplierCountry': 'Vendi i furnizuesit',
  'cbam.prec.period': 'Periudha raportuese',
  'cbam.prec.periodHint': 'Viti i prodhimit që mbulojnë vlerat e furnizuesit, p.sh. 2026.',
  'cbam.prec.originOperator': 'Operatori i origjinës',
  'cbam.prec.originInstallation': 'Instalimi i origjinës',
  'cbam.prec.originRegistryId': 'ID e regjistrit CBAM',
  'cbam.prec.evidence': 'Referenca e dokumentit',
  'cbam.prec.evidenceHint': 'Raporti i verifikuar i furnizuesit që i mbështet këto vlera.',
  'cbam.prec.supplierHint': 'Vlerat e furnizuesit pranohen vetëm nëse janë verifikuar nga një verifikues i akredituar në BE — ruani referencën e dokumentit.',
  'cbam.prec.country': 'Vendi i prodhimit',
  'cbam.prec.unknownWarn': 'Kur vendi i prodhimit nuk identifikohet dot, zbatohet vlera ndëshkuese default — më e larta ndër të gjitha vendet, sipas Aneksit IV të Rregullores zbatuese (BE) 2025/2621 (p.sh. 7601 = 3.198 tCO₂e/t bazë). Nëse e dini vendin, zgjidheni.',
  'cbam.prec.remove': 'Hiq',

  // "Produced in this installation" — the third value source. Entry-surface
  // labels stay Albanian; the explanatory Callout below quotes the regulation
  // mechanism and stays English in both dictionaries, like cbam.lookup.*.
  'cbam.prec.internal': 'Prodhuar në këtë instalim',
  'cbam.prec.internalShort': 'Prodhuar këtu',
  'cbam.prec.sourceGood': 'Malli burim',
  'cbam.prec.internalCnHint': 'Merret nga malli burim.',
  'cbam.prec.tptInternalHint': 'Masa bruto hyrëse — përfshi edhe copëzat/skrapin që nuk përfundon në mallin final (Aneksi III, pika B).',
  'cbam.prec.internalEvidenceHint': 'Regjistrimi i brendshëm i prodhimit që mbështet sasinë.',
  'cbam.prec.noSourceGoods': 'S\'ka mall tjetër të vitit {year} që mund të shërbejë si burim — krijoni fillimisht mallin burim (në një proces tjetër prodhimi), ruajeni, pastaj lidheni këtu.',
  'cbam.prec.internalHint': 'Embedded emissions for this row are resolved at calculation time from the source good\'s own calculated SEE — direct and indirect separately — per Annex II point E(1) of IR (EU) 2025/2547, as part of the operator\'s monitoring and calculation duties under Regulation (EU) 2023/956.',

  // The CN-lookup read-out quotes the regulation and feeds English artefacts,
  // so it stays English in BOTH dictionaries (same decision as cbam.overview.*).
  'cbam.lookup.checking': 'Checking the CN code…',
  'cbam.lookup.inAnnex': 'Annex I good',
  'cbam.lookup.notInAnnex': 'CN {code} is not in Annex I — it is not a CBAM good and owes no certificates.',
  'cbam.lookup.indirectMemo': 'Only DIRECT embedded emissions owe certificates for this sector — indirect (electricity) emissions are reported as a memo.',
  'cbam.lookup.indirectCharged': 'Direct AND indirect embedded emissions are both chargeable for this sector.',
  'cbam.lookup.default': 'Default value {year}',
  'cbam.lookup.fallback': 'No country-specific default — the "Other countries" fallback applies',
  'cbam.lookup.benchmark': 'CBAM benchmark',

  // ── Legacy-page sweep ──
  'regw.tokenMissing': 'Linku i regjistrimit është i pavlefshëm. Ju lutemi kontaktoni mbështetjen.',
  'regw.tokenInvalid': 'Linku i regjistrimit është i pavlefshëm.',
  'regw.tokenExpired': 'Linku i regjistrimit ka skaduar. Ju lutemi kontaktoni mbështetjen për një link të ri.',
  'regw.tokenUsed': 'Ky link i regjistrimit është përdorur tashmë. Nëse keni nevojë për ndihmë, kontaktoni mbështetjen.',
  'regw.tokenVerifyFailed': 'Gabim gjatë verifikimit të linkut. Ju lutemi provoni përsëri ose kontaktoni mbështetjen.',
  'regw.requiredField': 'Kjo fushë është e detyrueshme',
  'regw.invalidEmail': 'Email i pavlefshëm',
  'regw.passwordMinLength': 'Fjalëkalimi duhet të jetë së paku 6 karaktere',
  'regw.passwordsMismatch': 'Fjalëkalimet nuk përputhen',
  'regw.invalidYear': 'Vit i pavlefshëm',
  'regw.mustBePositive': 'Duhet të jetë një numër pozitiv',
  'regw.mustBeWholeNumberMin': 'Duhet të jetë një numër i plotë >= 1',
  'regw.registrationSuccess': 'Regjistrimi u përfundua me sukses! Ju jeni identifikuar automatikisht.',
  'regw.registrationFailed': 'Gabim gjatë regjistrimit. Ju lutemi provoni përsëri.',
  'regw.verifyingLink': 'Duke verifikuar linkun e regjistrimit...',
  'regw.pleaseWait': 'Ju lutemi prisni...',
  'regw.invalidLinkTitle': 'Link i pavlefshëm',
  'regw.contactSupportHelp': 'Nëse keni nevojë për ndihmë, ju lutemi kontaktoni mbështetjen tonë.',
  'regw.step1Title': 'Krijo llogarinë',
  'regw.step1Subtitle': 'Mbushni të dhënat e llogarisë tuaj për të krijuar profilin.',
  'regw.typeHere': 'Shkruani këtu',
  'regw.passwordLabel': 'Fjalëkalimi',
  'regw.confirmPasswordLabel': 'Konfirmo fjalëkalimin',
  'regw.saveAndContinue': 'Ruaj dhe vazhdo',
  'regw.step2Title': 'Kompania',
  'regw.step2Subtitle': 'Mbushni të dhënat e kompanisë tuaj për të krijuar profilin',
  'regw.companyNameLabel': 'Emri i kompanisë',
  'regw.addressLabel': 'Adresa',
  'regw.cityLabel': 'Qyteti',
  'regw.selectCity': 'Selekto qytetin',
  'regw.countryLabel': 'Shteti',
  'regw.selectCountry': 'Selekto shtetin',
  'regw.countryKosovo': 'Kosova',
  'regw.countryAlbania': 'Shqipëri',
  'regw.countryNorthMacedonia': 'Maqedoni e Veriut',
  'regw.countryMontenegro': 'Mali i Zi',
  'regw.companyTypeLabel': 'Lloji i kompanisë',
  'regw.selectType': 'Selekto llojin',
  'regw.companySectorLabel': 'Sektori i kompanisë',
  'regw.selectSector': 'Selekto sektorin',
  'regw.sectorAgriculture': 'Bujqësi',
  'regw.sectorConstruction': 'Ndërtimtari',
  'regw.sectorTechnology': 'Teknologji',
  'regw.sectorServices': 'Shërbime',
  'regw.production': 'Prodhim',
  'regw.sectorTrade': 'Tregti',
  'regw.onsiteActivitiesLabel': 'Aktivitetet e kompanisë on-site',
  'regw.processing': 'Përpunim',
  'regw.assembly': 'Montim',
  'regw.retail': 'Shitje me pakicë',
  'regw.wholesale': 'Shitje me shumicë',
  'regw.offsiteActivitiesLabel': 'Aktivitetet e kompanisë off-site',
  'regw.distribution': 'Shpërndarje',
  'regw.logistics': 'Logjistikë',
  'regw.maintenance': 'Mirëmbajtje',
  'regw.sustainabilityTargetLabel': 'Targeti i qëndrueshmərisë',
  'regw.targetPlaceholder': 'Shkruani targetin',
  'regw.usagePurposeLabel': 'Qëllimi i përdorimit',
  'regw.reportingTypeLabel': 'Për qfarë lloj raportimi do e përdorni',
  'regw.goBack': 'Kthehu mbrapa',
  'regw.step3Title': 'Ndërtesat',
  'regw.step3Subtitle': 'Regjistroni ndërtesën tuaj',
  'regw.nameLabel': 'Emri',
  'regw.buildingLabel': 'Ndërtesa',
  'regw.buildingIndustrial': 'Objekt industrial',
  'regw.buildingCommercial': 'Objekt tregtar',
  'regw.buildingResidential': 'Objekt banimi',
  'regw.buildingAdministrative': 'Objekt administrativ',
  'regw.buildingMixed': 'Objekt të përzier',
  'regw.constructionYearLabel': 'Viti i ndërtimit',
  'regw.buildingSizeLabel': 'Madhësia e objektit',
  'regw.floorsLabel': 'Numri i kateve',
  'regw.primaryEnergyLabel': 'Burimi kryesor i energjisë',
  'regw.selectPrimarySource': 'Selekto burimin kryesor',
  'regw.energyGridElectricity': 'Energji elektrike nga rrjeti',
  'regw.energyNaturalGas': 'Gaz natyror',
  'regw.energyDistrictHeating': 'Ngrohje qendrore (rrjet i qytetit)',
  'regw.energyLpg': 'Gaz i lëngshëm (LPG – propan / butan)',
  'regw.energyHeatingOil': 'Naftë për ngrohje / dizel',
  'regw.energyBiomass': 'Biomasë (dru, peleta, etj.)',
  'regw.energyOnsiteRenewable': 'Energji e rinovueshme në objekt (diellore, erë, gjeotermale)',
  'regw.energyMixed': 'Burime të përziera energjie',
  'regw.energyUnknown': 'E panjohur',
  'regw.secondaryEnergyLabel': 'Burimi dytësor i energjisë (opsional)',
  'regw.none': 'Asnjë',
  'regw.waterSupplyLabel': 'Lloji i furnizimit me ujë',
  'regw.waterPublicNetwork': 'Rrjet publik',
  'regw.waterPrivateWell': 'Pus privat',
  'regw.waterNaturalSource': 'Burim natyror',
  'regw.finish': 'Përfundo',
  'admu.accessDenied': 'Aksesi i ndaluar. Vetëm administratorët mund të aksesojnë këtë faqe.',
  'admu.loadUsersFailed': 'Gabim gjatë ngarkimit të përdoruesve.',
  'admu.statusChanged': 'Statusi i përdoruesit u ndryshua në: {status}',
  'admu.statusChangeFailed': 'Gabim gjatë ndryshimit të statusit.',
  'admu.resetPasswordFailed': 'Gabim gjatë rivendosjes së fjalëkalimit.',
  'admu.planChangedArchived': 'Plani u ndryshua në {plan}. {count} ndërtesa u arkivuan.',
  'admu.planChanged': 'Plani i përdoruesit u ndryshua në: {plan}',
  'admu.planChangeFailed': 'Gabim gjatë ndryshimit të planit.',
  'admu.loadBuildingsFailed': 'Gabim gjatë ngarkimit të ndërtesave.',
  'admu.selectExactly': 'Duhet të zgjidhni saktësisht {count} ndërtesë për të vazhduar.',
  'admu.loadBuildingDetailsFailed': 'Gabim gjatë ngarkimit të detajeve të ndërtesave.',
  'admu.buildingRestored': 'Ndërtesa u rikthye me sukses.',
  'admu.restoreBuildingFailed': 'Gabim gjatë rikthimit të ndërtesës.',
  'admu.buildingNameEmpty': 'Emri i ndërtesës nuk mund të jetë bosh.',
  'admu.nameSaved': 'Emri u ruajt me sukses.',
  'admu.saveNameFailed': 'Gabim gjatë ruajtjes së emrit.',
  'admu.buildingArchived': 'Ndërtesa u arkivua me sukses.',
  'admu.archiveBuildingFailed': 'Gabim gjatë arkivimit të ndërtesës.',
  'admu.buildingNumber': 'Ndërtesa #{id}',
  'admu.userDeleted': 'Përdoruesi {username} u fshi me sukses.',
  'admu.deleteUserFailed': 'Gabim gjatë fshirjes së përdoruesit.',
  'admu.backToApp': 'Kthehu te aplikacioni',
  'admu.title': 'Përdoruesit',
  'admu.subtitle': 'Menaxho llogaritë e përdoruesve dhe planet e abonimit',
  'admu.searchPlaceholder': 'Kërko sipas emrit, email, ose username...',
  'admu.clearSearch': 'Pastro kërkimin',
  'admu.resultCount': '{count} rezultat',
  'admu.resultsCount': '{count} rezultate',
  'admu.user': 'Përdoruesi',
  'admu.contact': 'Kontakti',
  'admu.status': 'Statusi',
  'admu.plan': 'Plani',
  'admu.buildings': 'Ndërtesat',
  'admu.created': 'Krijuar',
  'admu.actions': 'Veprimet',
  'admu.noSearchResults': 'Nuk u gjetën rezultate për kërkimin',
  'admu.noUsers': 'Nuk u gjetën përdorues',
  'admu.archivedAbbrev': '+{count} ark.',
  'admu.disableUserTitle': 'Çaktivizo përdoruesin',
  'admu.disable': 'Çaktivizo',
  'admu.enableUserTitle': 'Aktivizo përdoruesin',
  'admu.enable': 'Aktivizo',
  'admu.setTempPasswordTitle': 'Cakto një fjalëkalim të përkohshëm',
  'admu.passwordButton': 'Fjalëkalim',
  'admu.deleteUserTitle': 'Fshi përdoruesin',
  'admu.delete': 'Fshi',
  'admu.pageOf': 'Faqja {current} nga {total}',
  'admu.tempPasswordTitle': 'Fjalëkalim i përkohshëm',
  'admu.tempPasswordSendTo': 'Dërgojani këto të dhëna',
  'admu.tempPasswordShownOnce': 'Ky fjalëkalim shfaqet vetëm tani dhe nuk mund të lexohet më vonë.',
  'admu.passwordLabel': 'Fjalëkalimi',
  'admu.clientMessage': 'Përshëndetje,\n\nHyni në Karbon360° me këto të dhëna:\n\nAdresa: https://app.karbon360.tech\nUsername: {username}\nFjalëkalimi: {password}\n\nJu lutemi ndryshoni fjalëkalimin pas hyrjes së parë.\n',
  'admu.copyClientMessage': 'Kopjo mesazhin për klientin',
  'admu.close': 'Mbyll',
  'admu.deleteUserHeading': 'Fshi Përdoruesin',
  'admu.disableUserHeading': 'Çaktivizo Përdoruesin',
  'admu.enableUserHeading': 'Aktivizo Përdoruesin',
  'admu.confirmDeletePre': 'A jeni të sigurt që dëshironi të fshini përdoruesin',
  'admu.cannotUndo': 'Ky veprim nuk mund të kthehet.',
  'admu.confirmDisablePre': 'A jeni të sigurt që dëshironi të çaktivizoni përdoruesin',
  'admu.confirmEnablePre': 'A jeni të sigurt që dëshironi të aktivizoni përdoruesin',
  'admu.noCancel': 'Jo, Anulo',
  'admu.yesContinue': 'Po, Vazhdo',
  'admu.loading': 'Duke ngarkuar...',
  'admu.activeBuildings': 'Ndërtesat Aktive',
  'admu.noActiveBuildings': 'Asnjë ndërtesë aktive',
  'admu.buildingNamePlaceholder': 'Emri i ndërtesës',
  'admu.save': 'Ruaj',
  'admu.cancel': 'Anulo',
  'admu.invoiceCount': '{count} fatura',
  'admu.emissionCount': '{count} emetime',
  'admu.archive': 'Arkivo',
  'admu.archivedBuildings': 'Ndërtesat e Arkivuara',
  'admu.noArchivedBuildings': 'Asnjë ndërtesë e arkivuar',
  'admu.archivedOn': 'Arkivuar: {date}',
  'admu.planLimitReached': 'Arrit limitin e planit. Rrisni planin për të rikthyer.',
  'admu.restoreBuilding': 'Rikthe ndërtesën',
  'admu.chooseBuildingsTitle': 'Zgjidhni Ndërtesat për të Mbajtur',
  'admu.dgHas': 'ka',
  'admu.dgActiveButPlan': 'ndërtesa aktive, por plani',
  'admu.dgAllowsOnly': 'lejon vetëm',
  'admu.downgradeWarning': 'Zgjidhni {count} ndërtesë për të mbajtur. Ndërtesat e tjera do të arkivohen (nuk do të fshihen).',
  'admu.loadingBuildings': 'Duke ngarkuar ndërtesat...',
  'admu.selected': 'Zgjedhur:',
  'admu.confirmChange': 'Konfirmo Ndryshimin',
  'admt.sessionExpired': 'Sesioni ka skaduar. Ju lutem hyni përsëri.',
  'admt.noAdminAccess': 'Ky llogari nuk ka akses administrativ.',
  'admt.loadFailed': 'Gabim gjatë ngarkimit të tokenëve.',
  'admt.inviteSent': 'Ftesa u dërgua te {email}',
  'admt.tokenGenerated': 'Token i ri u gjenerua me sukses!',
  'admt.generateFailed': 'Gabim gjatë gjenerimit të tokenit.',
  'admt.copied': 'Kopjuar në clipboard!',
  'admt.used': 'Përdorur',
  'admt.expired': 'Skaduar',
  'admt.active': 'Aktiv',
  'admt.statActive': 'Aktivë',
  'admt.pageTitle': 'Menaxhimi i Tokenëve',
  'admt.pageSubtitle': 'Gjenero dhe menaxho tokenët e regjistrimit për përdoruesit e rinj.',
  'admt.backToUsers': 'Kthehu te Përdoruesit',
  'admt.statTotal': 'Total Tokenë',
  'admt.generateNewToken': 'Gjenero Token të Ri',
  'admt.inviteEmailLabel': 'Email i të ftuarit',
  'admt.inviteEmailPlaceholder': 'emri@kompania.com',
  'admt.inviteNameLabel': 'Emri (opsional)',
  'admt.inviteNamePlaceholder': 'Emri i kontaktit',
  'admt.planLabel': 'Plani',
  'admt.planBasic': 'Basic · 1 ndërtesë',
  'admt.planAdvanced': 'Advanced · 3 ndërtesa',
  'admt.planCorporate': 'Corporate · 100 ndërtesa',
  'admt.expiresInDaysLabel': 'Skadon pas (ditë)',
  'admt.generateWithEmailTitle': 'Gjeneron tokenin dhe dërgon ftesën me email',
  'admt.generateOnlyTitle': 'Gjeneron vetëm tokenin (pa email)',
  'admt.sending': 'Duke dërguar...',
  'admt.sendInvite': 'Dërgo ftesën',
  'admt.generateToken': 'Gjenero Token',
  'admt.newTokenHeading': 'Token i Ri i Gjeneruar',
  'admt.inviteSentToPrefix': 'Ftesa u dërgua te',
  'admt.inviteSentNoManual': 'Nuk keni nevojë ta dërgoni linkun manualisht.',
  'admt.invite': 'Ftesa',
  'admt.notSent': 'nuk u dërgua',
  'admt.copyLinkManually': 'Kopjoni linkun më poshtë dhe dërgojeni manualisht.',
  'admt.registrationLinkLabel': 'Link i Regjistrimit:',
  'admt.copy': 'Kopjo',
  'admt.expiresOn': 'Skadon më:',
  'admt.existingTokens': 'Tokenë Ekzistues',
  'admt.tokenCount': '{count} tokenë',
  'admt.emptyTitle': 'Nuk ka tokenë të gjeneruar ende.',
  'admt.emptyHint': 'Gjeneroni një token të ri për të filluar.',
  'admt.thInviteFor': 'Ftesa për',
  'admt.thStatus': 'Statusi',
  'admt.thCreatedAt': 'Krijuar më',
  'admt.thExpiresAt': 'Skadon më',
  'admt.thUsedAt': 'Përdorur më',
  'bldp.pageTitle': 'Ndërtesat',
  'bldp.registrationTitle': 'REGJISTRIMI I NDËRTESËS',
  'bldp.addBuilding': 'Shto ndërtesë',
  'bldp.name': 'Emri',
  'bldp.building': 'Ndërtesa',
  'bldp.address': 'Adresa',
  'bldp.city': 'Qyteti',
  'bldp.country': 'Shteti',
  'bldp.constructionYear': 'Viti i ndërtimit',
  'bldp.areaSqm': 'Madhësia e objektit',
  'bldp.floors': 'Numri i kateve',
  'bldp.energySources': 'Burimet e energjisë në ndërtesë',
  'bldp.waterSupplyType': 'Lloji i furnizimit me ujë',
  'bldp.heatingCoolingType': 'Lloji i ngrohjes dhe ftohjes',
  'bldp.selectType': 'Selekto llojin',
  'bldp.selectSource': 'Selekto burimin',
  'bldp.typeCommercial': 'Objekt tregtar',
  'bldp.typeIndustrial': 'Objekt industrial',
  'bldp.typeResidential': 'Objekt banimi',
  'bldp.typeOffice': 'Zyrë',
  'bldp.energyGrid': 'Rrjet elektrik',
  'bldp.energyNaturalGas': 'Gaz natyror',
  'bldp.energyOil': 'Naftë',
  'bldp.energySolar': 'Energji diellore',
  'bldp.waterPublic': 'Rrjet publik',
  'bldp.waterWell': 'Pus',
  'bldp.waterIndependent': 'Sistem i pavarur',
  'bldp.heatingCentral': 'Qendror',
  'bldp.heatingIndividual': 'Individual',
  'bldp.heatingNone': 'Nuk ka',
  'bldp.saving': 'Duke ruajtur…',
  'bldp.addBuildingSubmit': 'Shto ndërtesën',
  'bldp.yourBuildings': 'Ndërtesat tuaja',
  'bldp.edit': 'Ndrysho',
  'bldp.delete': 'Fshij',
  'bldp.nameRequired': 'Emri është i detyrueshëm',
  'bldp.buildingTypeRequired': 'Lloji i ndërtesës është i detyrueshëm',
  'bldp.addressRequired': 'Adresa është e detyrueshme',
  'bldp.cityRequired': 'Qyteti është i detyrueshëm',
  'bldp.countryRequired': 'Shteti është i detyrueshëm',
  'bldp.yearRequired': 'Viti është i detyrueshëm',
  'bldp.areaRequired': 'Madhësia është e detyrueshme',
  'bldp.floorsRequired': 'Numri i kateve është i detyrueshëm',
  'bldp.energyRequired': 'Burimi i energjisë është i detyrueshëm',
  'bldp.waterRequired': 'Furnizimi me ujë është i detyrueshëm',
  'bldp.heatingRequired': 'Ngrohja/ftohja është e detyrueshme',
  'bldp.loadFailed': 'Gabim gjatë ngarkimit të ndërtesave.',
  'bldp.planLimit': 'Keni arritur limitin e ndërtesave për planin tuaj.',
  'bldp.addFailed': 'Gabim gjatë shtimit të ndërtesës.',
  'bldp.updateFailed': 'Gabim gjatë përditësimit të ndërtesës.',
  'bldp.deleteFailed': 'Gabim gjatë fshirjes së ndërtesës.',
  'facp.addFacility': 'Shto ndërtesë',
  'facp.nameRequired': 'Emri i ndërtesës është i detyrueshëm.',
  'facp.updated': 'Ndërtesa u përditësua',
  'facp.added': 'Ndërtesa u shtua',
  'facp.saveFailed': 'Nuk u ruajt. Provoni përsëri.',
  'facp.deleted': 'Ndërtesa u fshi',
  'facp.deleteFailed': 'Nuk u fshi',
  'facp.delete': 'Fshij',
  'facp.edit': 'Ndrysho',
  'facp.addData': 'Shto të dhëna',
  'facp.editFacility': 'Ndrysho ndërtesën',
  'facp.cancel': 'Anulo',
  'facp.saving': 'Duke ruajtur…',
  'facp.saveChanges': 'Ruaj ndryshimet',
  'facp.addFacilitySubmit': 'Shto ndërtesën',
  'facp.name': 'Emri',
  'facp.namePlaceholder': 'p.sh. Zyra qendrore',
  'facp.buildingType': 'Lloji i ndërtesës',
  'facp.selectType': 'Selekto llojin',
  'facp.address': 'Adresa',
  'facp.city': 'Qyteti',
  'facp.country': 'Shteti',
  'facp.constructionYear': 'Viti i ndërtimit',
  'facp.areaSqm': 'Madhësia e objektit',
  'facp.areaHint': 'Përdoret për intensitetin — pa të, intensiteti nuk llogaritet.',
  'facp.floors': 'Numri i kateve',
  'facp.energySources': 'Burimet e energjisë',
  'facp.energyPlaceholder': 'p.sh. Rrymë, gaz',
  'facp.heatingCooling': 'Ngrohja dhe ftohja',
  'facp.waterSupply': 'Furnizimi me ujë',
  'facp.typeOffice': 'Zyre',
  'facp.typeWarehouse': 'Depo / Magazinë',
  'facp.typeProduction': 'Prodhim',
  'facp.typeRetail': 'Shitje me pakicë',
  'facp.typeDataCenter': 'Qendër të dhënash',
  'facp.typeOther': 'Tjetër',
  's3p.modeWalk': 'Këmbë',
  's3p.modeBike': 'Biçikletë',
  's3p.modeBus': 'Autobus',
  's3p.modeRail': 'Tren/Tramvaj',
  's3p.modeCarSolo': 'Makina (Solo)',
  's3p.modeCarCarpool': 'Makina (Carpool)',
  's3p.enterEmployees': 'Ju lutem vendosni numrin total të punëtorëve',
  's3p.fillOneMode': 'Ju lutem plotësoni të paktën një mënyrë udhëtimi',
  's3p.error': 'Gabim: {message}',
  's3p.confirmBuilding': 'Ju lutem konfirmoni ndërtesën para se të ruani të dhënat',
  's3p.commuteSaved': 'U ruajtën {created} regjistrime{dupes} · Udhëtimet e punëtorëve · {period}.',
  's3p.commuteSavedDupes': '; {skipped} u shmangën si duplikate',
  's3p.saved': '{value} t CO₂e u shtua te Scope 3 · {period}.',
  's3p.saveFailedCheckBuilding': 'Ruajtja dështoi. Kontrolloni ndërtesën dhe provoni përsëri.',
  'mflow.title': 'Regjistrimi mujor',
  'mflow.stepStationary': 'Djegia stacionare',
  'mflow.stepMobile': 'Djegia mobile',
  'mflow.stepProcess': 'Emetimet nga proceset',
  'mflow.stepElectricity': 'Elektriciteti',
  'mflow.stepTravel': 'Udhëtimet (biznes + punëtorë)',
  'mflow.heading': 'Regjistrimi i të dhënave',
  'mflow.headingSub': 'Kalo hap pas hapi nëpër scope-t — raporti mujor plotësohet vetë.',
  'mflow.prevMonth': 'Muaji i mëparshëm',
  'mflow.nextMonth': 'Muaji tjetër',
  'mflow.stepsDone': '{done} / {total} hapa me të dhëna',
  'mflow.monthComplete': 'Muaji i plotë ✓',
  'mflow.summaryTitle': 'Përmbledhja e {month}',
  'mflow.summarySub': 'Të tri scope-t kanë të dhëna — muaji hyn i plotë në raport.',
  'mflow.total': 'Gjithsej',
  'mflow.biogenicMemo': 'CO₂ biogjenik (memo): {value} t — raportohet veçmas, jashtë totalit.',
  'mflow.viewReports': 'Shiko raportet',
  'mflow.viewAllInvoices': 'Shiko të gjitha faturat',
  'mflow.loading': 'Duke ngarkuar…',
  'mflow.ifApplicable': 'nëse aplikohet',
  'mflow.recordsOne': '{count} regjistrim · {total} t CO₂e',
  'mflow.recordsMany': '{count} regjistrime · {total} t CO₂e',
  'mflow.noData': 'Pa të dhëna për këtë muaj',
  'mflow.continueHere': 'Vazhdo këtu →',
  'mflow.addReview': 'Shto / rishiko',
  'mflow.addData': 'Shto të dhënat →',
  'mflow.footerLead': 'Ky ekran ndjek vetëm regjistrimin e muajit të zgjedhur. Pamjen e plotë e gjen te',
  'mflow.detailedReport': 'Raporti i detajuar',
  'mflow.and': 'dhe',
  's1fug.title': 'Scope 1 - Emetimet Fugjitive',
  's1fug.fillAllFields': 'Plotësoni të gjitha fushat para llogaritjes',
  's1fug.duplicate': 'Ky regjistrim ekziston tashmë për këtë periudhë',
  's1fug.saveError': 'Gabim gjatë ruajtjes: {message}',
  's1fug.building': 'Ndërtesa',
  's1fug.selectBuilding': 'Zgjidhni ndërtesën',
  's1fug.period': 'Periudha',
  's1fug.source': 'Burimi i emisioneve fugjitive',
  's1fug.selectSource': 'Zgjidhni burimin',
  's1fug.gasType': 'Lloji i gazit / ftohësit',
  's1fug.selectType': 'Zgjidhni llojin',
  's1fug.quantity': 'Sasia e humbur / emetuar (kg)',
  's1fug.qtyPlaceholder': 'p.sh. 2.5',
  's1fug.calculating': 'Duke llogaritur...',
  's1fug.calculate': 'Llogarit',
  's1fug.saving': 'Duke ruajtur...',
  's1fug.savedBadge': '✓ U ruajt',
  's1fug.save': 'Ruaj',
  's1fug.clear': 'Pastro',
  's1fug.result': 'Rezultati i llogaritjes',
  's1fug.gasRefrigerant': 'Gazi / Ftohësi',
  's1fug.emissions': 'Emetimet',
  's1dj.sessionExpired': 'Sesioni ka skaduar. Ju lutem hyni përsëri.',
  's1dj.saveFailed': 'Ruajtja dështoi. Provoni përsëri.',
  's1dj.liters': 'Litra',
  's1dj.biogenicSeparate': 'Biogenic CO₂ ({period}): {value} t CO₂e — raportuar veçmas, jo në Scope 1',
  'invp.pickBuildingBeforeInvoice': 'Ju lutem zgjidhni ndërtesën para se të ngarkoni faturën',
  'invp.pickBuildingBeforeInvoices': 'Ju lutem zgjidhni ndërtesën para se të ngarkoni faturat',
  'invp.pickBuilding': 'Ju lutem zgjidhni një ndërtesë',
  'invp.sessionExpired': 'Sesioni ka skaduar. Ju lutem hyni përsëri.',
  'invp.saveError': 'Gabim gjatë ruajtjes: {message}',
  'invp.saveRetry': 'Ruajtja dështoi. Provoni përsëri.',
  'invnp.detailsLoadFailed': 'Gabim gjatë ngarkimit të detajeve të faturës',
  'invnp.allTab': 'TË GJITHA',
  'invnp.thisInvoice': 'këtë faturë',
  'invnp.confirmDelete': 'Fshi "{label}"? Ky veprim nuk mund të kthehet.',
  'invnp.deleteFailed': 'Fshirja dështoi',
  'invnp.errorWithMessage': 'Gabim: {message}',
  'invnp.colSource': 'Burimi',
  'invnp.scanned': 'E skanuar',
  'invnp.colBuilding': 'Ndërtesa',
  'invnp.colEmissions': 'Emetimet CO₂',
  'invnp.tabStationary': 'Djegja Stacionare',
  'invnp.tabMobile': 'Djegja Mobile',
  'invnp.tabProcess': 'Emetimet nga proceset',
  'invnp.tabElectricity': 'Elektricitet',
  'invnp.pageTitle': 'Regjistr i Faturave',
  's1mob.pickBuildingFirst': 'Ju lutem zgjidhni ndërtesën para se të vazhdoni',
  's1mob.litres': 'Litra',
  'recp.title': 'Faturat',
  'recp.allCategories': 'Të gjitha kategoritë',
  'recp.catScope1Electricity': 'Scope 1 - Elektricitet',
  'recp.catScope1Mobile': 'Scope 1 - Djegie Mobile',
  'recp.catScope1Stationary': 'Scope 1 - Djegie Stacionare',
  'recp.catScope1Heating': 'Scope 1 - Ngrohje',
  'recp.catScope1Ev': 'Scope 1 - Automjete Elektrike',
  'recp.catScope2Electricity': 'Scope 2 - Elektricitet',
  'recp.catScope3Travel': 'Scope 3 - Udhëtime Biznesore',
  'recp.listTitle': 'Lista e Faturave',
  'recp.colSelect': 'Selekto',
  'recp.colName': 'Emri (Name)',
  'recp.colDate': 'Data (Date)',
  'recp.colView': 'Shiko (View)',
  'recp.colDelete': 'Fshij (Delete)',
  'recnp.tabScope1Stationary': 'Scope 1 - Djegja Stacionare',
  'recnp.tabScope1Mobile': 'Scope 1 - Djegja Mobile',
  'recnp.tabScope1Process': 'Scope 1 - Emetimet nga proceset',
  'recnp.pageTitle': 'Recetat',
  'repp.pageTitle': 'Raporti i detajuar',
  'repp.colLabel': 'Etiketa',
  'repp.colRecords': 'Regjistrime',
  'repp.colQuantity': 'Sasia',
  'repp.colEmissions': 'Emetimet (tCO₂e)',
  'repp.colSubtotalPct': '% e nën-totalit',
  'repp.colScopePct': '% e Scope',
  'repp.periodLabel': 'Periudha:',
  'repp.buildingLabel': 'Ndërtesa:',
  'repp.allBuildings': 'Të gjitha',
  'repp.scope1Title': 'Scope 1 — Emetimet direkte',
  'repp.stationaryByFuel': 'Djegja Stacionare — sipas karburantit',
  'repp.mobileByVehicle': 'Djegja Mobile — sipas automjetit',
  'repp.processByType': 'Proceset — sipas tipit',
  'repp.scope2Title': 'Scope 2 — Emetimet indirekte (energji)',
  'repp.byConsumptionType': 'Sipas tipit të konsumit',
  'repp.scope3Title': 'Scope 3 — Emetimet indirekte (zinxhiri i vlerës)',
  'repp.byActivity': '{label} — sipas aktivitetit',
  'repp.biogenicNote': 'Reported for transparency, not included in Scope 1 — sipas GHG Protocol & ISO 14064.',
  'repp.byBiogenicFuel': 'Sipas karburantit biogjenik',
  'dashp.logoUploadFailed': 'Ngarkimi i logos dështoi',
  'dashp.logoRemoveFailed': 'Heqja e logos dështoi',
  'dashp.scope2Elec': 'Scope 2 (elektriciteti)',
  'dashp.and': 'dhe',
  'dashp.stationary': 'Djegja stacionare',
  'dashp.mobile': 'Djegja mobile',
  'dashp.electricity': 'Elektriciteti',
  'dashp.heating': 'Nxemja',
  'dashp.evCharging': 'Veturat elektrike',
  'dashp.stationaryByFuel': 'Djegja stacionare — sipas karburantit',
  'dashp.mobileByVehicle': 'Djegja mobile — sipas automjetit',
  'dashp.processByType': 'Proceset — sipas tipit',
  'dashp.scope2ByType': 'Scope 2 — sipas tipit',
  'invdet.fileGone': 'Dokumenti origjinal nuk është më i disponueshëm në server.',
  'invdet.previewFailed': 'Nuk mund ta ngarkojmë dokumentin.',
  'invdet.typeLabel': 'LLOJI:',
  'invdet.vehicle': 'Automjet',
  'invdet.methodLabelColon': 'METODA:',
  'invdet.methodLabel': 'METODA',
  'invdet.fuelDetails': 'Detajet e karburantit',
  'invdet.fuelType': 'Lloji i karburantit',
  'invdet.quantity': 'Sasia',
  'invdet.period': 'Periudha',
  'invdet.emissions': 'Emetimet',
  'invdet.supplierLabel': 'FURNIZUES:',
  'invdet.registers': 'Regjistrat',
  'invdet.comparison': 'Krahasimi',
  'invdet.prevMonth': 'Muaji përsipër.',
  'invdet.noDataLine1': 'Nuk ka te',
  'invdet.noDataLine2': 'dhena',
  'invdet.currMonth': 'Muaji i fundit',
  'invdet.electricityUse': 'Shpenzimi i rrymës',
  'invdet.carbonEmissions': 'Emetimet e karbonit',
  'invdet.loadingDoc': 'Dokumenti po ngarkohet...',
  'invdet.noAttachment': 'Nuk ka dokument të bashkangjitur',
  'invdet.categoryLabel': 'KATEGORIA:',
  'invdet.activityDetails': 'Detajet e aktivitetit',
  'invdet.sourceOcr': 'Ky regjistrim u krijua automatikisht nga skanimi i faturës',
  'invdet.sourceManual': 'Ky regjistrim u shtua manualisht',
  'eflow.verifyTitle': 'Verifiko llogaritjen',
  'eflow.savedTitle': 'Aktiviteti u regjistrua',
  'eflow.closeNoSave': 'Mbyll pa ruajtur',
  'eflow.close': 'Mbyll',
  'eflow.attachedDoc': 'Dokumenti i bashkangjitur',
  'eflow.document': 'Dokumenti',
  'eflow.scannedDoc': 'Dokumenti i skanuar',
  'eflow.saving': 'Duke ruajtur…',
  'eflow.save': 'Ruaj',
  'eflow.backAndFix': 'Kthehu & korrigjo',
  'eflow.savedSuccess': 'U ruajt me sukses',
  'eflow.monthCoverage': 'Mbulueshmëria e muajit',
  'eflow.scopeComplete': 'Ky Scope është i plotë për muajin ✓',
  'eflow.continueTo': 'Vazhdo te {label} →',
  'eflow.addAnother': 'Shto aktivitet të ri',
  'eflow.finish': 'Mbaroj',
  'dtbl.noData': 'Nuk ka të dhëna',
  'dtbl.delete': 'Fshi',
  'tabf.all': 'Të gjitha',
  'appx.sessionExpired': 'Sesioni juaj ka skaduar. Ju lutem hyni përsëri.',
  'appx.resetLinkInvalid': 'Linku i rivendosjes është i pavlefshëm.',
  'appx.resetLinkExpired': 'Linku i rivendosjes ka skaduar ose është përdorur.',
  'appx.resetLinkVerifyError': 'Gabim gjatë verifikimit të linkut.',
  'appx.passwordMin': 'Fjalëkalimi duhet të jetë së paku 6 karaktere.',
  'appx.passwordMismatch': 'Fjalëkalimet nuk përputhen.',
  'appx.passwordChanged': 'Fjalëkalimi u ndryshua me sukses. Ju lutem hyni përsëri.',
  'appx.resetFailed': 'Rivendosja e fjalëkalimit dështoi.',
  'appx.resetTitle': 'Rivendos fjalëkalimin',
  'appx.validatingLink': 'Po verifikohet linku...',
  'appx.newPassword': 'Fjalëkalimi i ri',
  'appx.confirmPassword': 'Konfirmo fjalëkalimin',
  'appx.savePassword': 'Ruaj fjalëkalimin',
  'appx.backToLogin': 'Kthehu te login',
  'purpose.spaceHeating': 'Ngrohje e ambientit',
  'purpose.hotWater': 'Ujë i ngrohtë sanitar',
  'purpose.processHeat': 'Nxehtësi procesi',
  'purpose.backupGenerator': 'Gjenerator rezervë',
};

export default sq;
